<!-- eslint-disable vue/html-self-closing -->
<template>
  <div
    class="project-page__create-fork mt-3"
    ref="projectCreateForkForm"
  >
    <gf-custom-alert
      v-if="!canCreateProject"
      variant="warning"
      element-class="mb-3"
    >
      <template
        v-if="applicationVersion === 'cloudweb'"
        #body
      >
        {{ $t('project.cannotCreate') }}
        <a
          href="mailto:support@gitflic.ru"
          class="text-warning"
        >
          support@gitflic.ru
        </a>
      </template>
      <template
        v-else
        #body
      >
        {{ $t('project.cannotCreateSelfhosted') }}
      </template>
    </gf-custom-alert>
    <div class="form-row mb-2">
      <div class="form-group col-md-4">
        <label
          class="font-weight-bolder"
          for="ownerAlias"
        >
          {{ $t('message.owner') }}
        </label>
        <div>
          <project-owner-selector @input="setOwner" />
        </div>
        <small
          v-if="
            formValidationErrors?.owner ||
            formValidationErrors?.aliasType 
          "
          class="text-danger"
        >
          {{
            formValidationErrors?.owner ||
            formValidationErrors?.aliasType 
          }}
        </small>
      </div>
      <div class="form-group col-md-8">
        <label
          class="gf-counted-input"
          for="projectTitle"
          :data-current-length="form.projectTitle?.length + ' / 100'"
        >
          <b>{{ $t('project.title') }}</b>
          <input
            id="projectTitle"
            ref="projectTitleInput"
            v-model="form.projectTitle"
            class="form-control form-control-sm --with-selector"
            :class="{ 'border-danger': formErrors.projectTitle || formValidationErrors?.title }"
            name="projectTitle"
            type="text"
            inputmode="text"
            minlength="1"
            maxlength="100"
            required="required"
            :placeholder="$t('project.titlePlaceholder')"
            @input="onProjectTitleInput"
          />
        </label>
        <small
          v-if="formValidationErrors?.title"
          class="text-danger"
        >
          {{ formValidationErrors?.title }}
        </small>
      </div>
    </div>
    <div class="form-group">
      <label
        for="language"
        class="font-weight-bolder"
      >
        {{ $t('project.language') }}
      </label>
      <project-language-selector @input="setLanguage" />
      <small
        v-if="formValidationErrors?.language"
        class="text-danger"
      >
        {{ formValidationErrors?.language }}
      </small>
    </div>
    <div class="form-group">
      <label
        class="font-weight-bolder"
        for="projectAlias"
      >
        {{ $t('project.url') }}
      </label>
      <gf-custom-translitor
        v-model="form.projectAlias"
        :text-to-translit="form?.projectTitle"
        :prepended-text="baseUrl + '/project/' + form?.ownerAlias + '/'"
        :has-error="formValidationErrors?.alias"
      />
      <small
        v-if="formValidationErrors?.alias"
        class="text-danger"
      >
        {{ formValidationErrors.alias }}
      </small>
    </div>
    <div class="form-group border-bottom pb-4">
      <label
        class="gf-counted-input"
        for="projectDescription"
        :data-current-length="form.projectDescription?.length + ' / 600'"
      >
        <b>{{ $tc('message.description', 0) }}</b>
        <input
          id="projectDescription"
          v-model="form.projectDescription"
          type="text"
          name="projectDescription"
          inputmode="text"
          maxlength="600"
          :placeholder="$tc('message.description', 0)"
          class="form-control form-control-sm mt-2"
          :class="{ 'border-danger': formValidationErrors?.description }"
        />
      </label>
      <small
        v-if="formValidationErrors?.description"
        class="text-danger"
      >
        {{ formValidationErrors?.description }}
      </small>
    </div>

    <div class="d-flex flex-column form-group pb-2 border-bottom">
      <label
        class="gf-custom-radio"
        for="publicProject"
      >
        <input
          id="publicProject"
          v-model="form.isPrivate"
          type="radio"
          name="projectPrivacy"
          :disabled="!canCreatePublic || forkIsPrivate"
          :value="false"
        />
        <div>
          <div :class="{ 'text-muted': !canCreatePublic || forkIsPrivate }">
            <span class="d-flex align-items-center">
              <i class="gf-icon gf-icon-book-open mr-2" />
              <b>{{ $t('project.public') }}</b>
            </span>
            <small class="form-text text-muted">
              <span v-if="canCreatePublic">{{ $t('project.publicDescription') }}</span>
              <span v-else-if="isOauth2">
                {{ $tc('project.noneOauth2', 1) }}
                <a :href="baseUrl + '/settings/account'">{{ $tc('project.noneOauth2', 0) }}</a>
              </span>
              <span v-else>{{ $t('functionUnavailable') }}</span>
              <span v-if="forkIsPrivate">
                {{ $t('fork.isPrivate') }}
              </span>
            </small>
          </div>
        </div>
      </label>
      <small
        v-if="formValidationErrors?.isPrivate"
        class="text-danger"
      >
        {{ formValidationErrors?.isPrivate }}
      </small>
      <label
        class="gf-custom-radio"
        for="privateProject"
      >
        <input
          id="privateProject"
          v-model="form.isPrivate"
          type="radio"
          name="projectPrivacy"
          :value="true"
          checked
        />
        <div>
          <div>
            <span class="d-flex align-items-center">
              <i class="gf-icon gf-icon-lock mr-2" />
              <b>{{ $t('project.private') }}</b>
            </span>
            <small class="form-text text-muted">
              {{ $t('project.privateDescription') }}
            </small>
          </div>
        </div>
      </label>
      <small
        v-if="formValidationErrors?.isPrivate"
        class="text-danger"
      >
        {{ formValidationErrors?.isPrivate }}
      </small>
    </div>

    <button
      class="btn btn-sm btn-success float-right"
      type="submit"
      :disabled="!canCreateProject || checkFormErrors || isForkCreating"
      @click="submit"
    >
      {{ $t('project.create') }}
    </button>
  </div>
</template>

<script>
import ProjectOwnerSelector from '@/shared-modules/shared-components/shared/project-owner-selector';
import GfCustomTranslitor from '@/shared-modules/shared-components/shared/GfCustomTranslitor';
import ProjectCreateService from '@/shared-modules/modules/project-create/project.create.service';
import { FORM_NAME_ENUMS } from '@/shared-modules/variables/form-name.enums';
import ProjectCreateMessages from '@/shared-modules/modules/project-create/project.create.messages';
import GfCustomAlert from '@/shared-modules/shared-components/shared/GfCustomAlert';

export default {
  // eslint-disable-next-line vue/multi-word-component-names, vue/match-component-file-name
  name: 'ProjectCreateFork',
  components: { ProjectOwnerSelector, GfCustomTranslitor, GfCustomAlert },
  data() {
    return {
      baseUrl: null,
      username: null,
      canCreatePublic: false,
      isOauth2: false,
      projectTitle: null,
      language: null,
      forkIsPrivate: true,
      canCreateProject: false,
      isForkCreating: false,
      form: {
        projectTitle: '',
        projectAlias: '',
        projectDescription: '',
        ownerAlias: '',
        ownerType: '',
        language: 'markdown',
        isPrivate: true
      },
      formErrors: {
        projectTitle: false,
        projectAlias: false
      }
    };
  },
  computed: {
    checkProjectTitleErrors() {
      return this.form.projectTitle?.length === 0 || this.form.projectTitle?.length > 100;
    },
    checkProjectAliasErrors() {
      return this.form.projectAlias?.length === 0 || this.form.projectAlias?.length > 100;
    },
    checkFormErrors() {
      return this.checkProjectTitleErrors || this.checkProjectAliasErrors;
    },
    formValidationErrors() {
      return this.$store.getters.getFormErrors(FORM_NAME_ENUMS.FORK_CREATE_FORM);
    },
    applicationVersion() {
      return this.$store.getters.getApplicationVersion;
    }
  },
  mounted() {
    this.resetErrors();
    this.form.projectTitle = `${this.$route?.params?.projectAlias}-форк`;
    this.baseUrl = document.querySelector('input[name=baseUrl]')?.value;
    this.username = document.querySelector('input[name=username]')?.value;
    this.canCreatePublic = document.querySelector('input[name=canCreatePublic]')?.value === 'true';
    this.isOauth2 =
      document.querySelector('input[name=isOauth2Vk]')?.value === 'true' ||
      (document.querySelector('input[name=isOauth2Yandex]')?.value === 'true' &&
        document.querySelector('input[name=isCompanyTariffication]')?.value === 'true');
    this.forkIsPrivate = document.querySelector('input[name=forkIsPrivate]')?.value === 'true';
    this.canCreateProject =
      document.querySelector('input[name=canCreateProject]')?.value === 'true';
    this.$refs.projectTitleInput.focus();
    this.$refs.projectCreateForkForm.addEventListener('keydown', this.onKeydownSubmit);
  },
  methods: {
    setOwner(e) {
      this.form.ownerAlias = e?.alias;
      this.form.ownerType = e?.ownerType;
    },
    setLanguage(e) {
      this.form.language = e?.title;
    },
    submit() {
      this.isForkCreating = true;
      this.resetErrors();
      const formData = {
        owner: this.form.ownerAlias,
        aliasType: this.form.ownerType,
        title: this.form.projectTitle,
        alias: this.form.projectAlias,
        description: this.form.projectDescription,
        language: this.form.language,
        isPrivate: this.form.isPrivate
      };
      ProjectCreateService.createFork(
        this.$route.params.alias,
        this.$route.params.projectAlias,
        formData
      )
        .then((res) => {
          window.location.replace(`/project/${res.ownerAlias}/${res.alias}`);
        })
        .catch(() => {
          this.isForkCreating = false;
        });
    },
    onProjectTitleInput() {
      this.formErrors.projectTitle = this.checkProjectTitleErrors;
    },
    onKeydownSubmit(e) {
      if (
        e.keyCode == 13 &&
        (e.metaKey || e.ctrlKey) &&
        this.canCreateProject &&
        !this.checkFormErrors &&
        !this.isForkCreating
      ) {
        this.submit();
      }
    },
    resetErrors() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.FORK_CREATE_FORM,
        errorResponse: null
      });
    }
  },
  beforeDestroy() {
    this.$refs.projectCreateForkForm.removeEventListener('keydown', this.onKeydownSubmit);
  },
  i18n: {
    messages: ProjectCreateMessages
  }
};
</script>
